import React, { useEffect, useMemo, useState } from "react";
import SafeIcon from "../../common/SafeIcon";
import {
  FiAlertCircle,
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiRefreshCw,
  FiTrendingUp,
} from "react-icons/fi";
import { useClient } from "../../context/ClientContext";
import { clientInvoiceService, invoiceStatus } from "../../services/client/clientInvoiceService";
import ClientInvoiceFilters from "../../components/clients/invoices/ClientInvoiceFilters";
import ClientInvoiceTable from "../../components/clients/invoices/ClientInvoiceTable";
import ClientInvoiceDetailsModal from "../../components/clients/modals/ClientInvoiceDetailsModal";

const PAGE_SIZE = 20;

function formatMoney(value, currency) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(value / 100);
  } catch {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
    }).format(value / 100);
  }
}

function SummaryCard({ label, value, currency, icon, tone, count }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p className="mt-2 truncate text-2xl font-black tracking-tight text-slate-900">
            {label === "Total Invoices"
              ? value.toLocaleString()
              : formatMoney(value, currency)}
          </p>
          {count !== undefined && (
            <p className="mt-1 text-xs text-slate-400">{count} invoices</p>
          )}
        </div>
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tone}`}>
          <SafeIcon icon={icon} className="text-lg" />
        </span>
      </div>
    </section>
  );
}

function filteredStatus(invoice) {
  const status = invoiceStatus(invoice);

  if (status === "voided" || status === "canceled" || status === "cancelled") {
    return "void";
  }
  if (status === "open" || status === "unpaid") return "sent";
  return status;
}

export default function ClientInvoices() {
  const { activeWorkspace } = useClient();
  const organizationId = activeWorkspace?.id || null;
  const organizationName = activeWorkspace?.name || "Your business";
  const currency = String(activeWorkspace?.currency || "USD").toUpperCase();

  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({
    totalInvoices: 0,
    totalInvoiced: 0,
    outstanding: 0,
    overdue: 0,
  });
  const [loadedOrganizationId, setLoadedOrganizationId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    startDate: "",
    endDate: "",
    sortBy: "newest",
  });

  useEffect(() => {
    let current = true;

    if (!organizationId) {
      setInvoices([]);
      setSummary({
        totalInvoices: 0,
        totalInvoiced: 0,
        outstanding: 0,
        overdue: 0,
      });
      setLoadedOrganizationId(null);
      setLoading(false);
      setError("Select a business to view its invoices.");
      setSelectedInvoice(null);
      return () => {
        current = false;
      };
    }

    setInvoices([]);
    setSummary({
      totalInvoices: 0,
      totalInvoiced: 0,
      outstanding: 0,
      overdue: 0,
    });
    setLoadedOrganizationId(null);
    setLoading(true);
    setRefreshing(false);
    setError("");
    setSelectedInvoice(null);
    setPage(1);

    clientInvoiceService
      .getClientInvoices({ organizationId })
      .then((result) => {
        if (!current) return;
        setInvoices(result.invoices);
        setSummary(result.summary);
        setLoadedOrganizationId(organizationId);
      })
      .catch((loadError) => {
        if (!current) return;
        setError(loadError?.message || "Unable to load invoices for this business.");
      })
      .finally(() => {
        if (current) setLoading(false);
      });

    return () => {
      current = false;
    };
  }, [organizationId]);

  const filteredInvoices = useMemo(() => {
    if (loadedOrganizationId !== organizationId) return [];

    const query = filters.search.trim().toLocaleLowerCase();

    return invoices
      .map((invoice) => ({
        ...invoice,
        displayStatus: invoiceStatus(invoice),
      }))
      .filter((invoice) => {
        const normalized = filteredStatus(invoice);

        if (filters.status !== "all" && normalized !== filters.status) {
          return false;
        }
        if (filters.startDate && invoice.issue_date < filters.startDate) {
          return false;
        }
        if (filters.endDate && invoice.issue_date > filters.endDate) {
          return false;
        }
        if (!query) return true;

        const searchable = [
          invoice.invoice_number,
          invoice.customer?.name,
          invoice.customer?.company_name,
          invoice.customer?.email,
        ]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase();

        return searchable.includes(query);
      })
      .sort((first, second) => {
        if (filters.sortBy === "due") {
          return String(first.due_date || "").localeCompare(String(second.due_date || ""));
        }
        if (filters.sortBy === "amount") {
          return (Number(second.total_amount) || 0) - (Number(first.total_amount) || 0);
        }

        const comparison = String(first.issue_date || "").localeCompare(
          String(second.issue_date || ""),
        );

        return filters.sortBy === "oldest" ? comparison : -comparison;
      });
  }, [filters, invoices, loadedOrganizationId, organizationId]);

  const pageCount = Math.max(1, Math.ceil(filteredInvoices.length / PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [filters, organizationId]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const updateFilter = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const resetFilters = () => {
    setFilters({
      search: "",
      status: "all",
      startDate: "",
      endDate: "",
      sortBy: "newest",
    });
  };

  const refreshInvoices = async () => {
    if (!organizationId || refreshing) return;

    setRefreshing(true);
    setError("");

    try {
      const result = await clientInvoiceService.getClientInvoices({ organizationId });
      if (loadedOrganizationId !== organizationId) return;
      setInvoices(result.invoices);
      setSummary(result.summary);
    } catch (refreshError) {
      setError(refreshError?.message || "Unable to refresh invoices.");
    } finally {
      setRefreshing(false);
    }
  };

  const showingData = loadedOrganizationId === organizationId;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-violet-700">
            Financial management
          </p>
          <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-900">
            Invoices
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Manage and track your business invoices and customer payments.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Selected business
            </p>
            <p className="mt-1 max-w-xs truncate text-sm font-bold text-slate-800">
              {organizationName}
            </p>
          </div>
          <button
            type="button"
            onClick={refreshInvoices}
            disabled={!organizationId || loading || refreshing}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-xs font-bold text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <SafeIcon
              icon={FiRefreshCw}
              className={refreshing ? "animate-spin" : ""}
            />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </header>

      {error && (
        <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <div className="flex items-start gap-2">
            <SafeIcon icon={FiAlertTriangle} className="mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">Invoices could not be loaded</p>
              <p className="mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          label="Total Invoices"
          value={showingData ? summary.totalInvoices : 0}
          currency={currency}
          icon={FiFileText}
          tone="bg-violet-50 text-violet-700"
          count={showingData ? summary.totalInvoices : 0}
        />
        <SummaryCard
          label="Total Invoiced Amount"
          value={showingData ? summary.totalInvoiced : 0}
          currency={currency}
          icon={FiTrendingUp}
          tone="bg-blue-50 text-blue-700"
        />
        <SummaryCard
          label="Outstanding Balance"
          value={showingData ? summary.outstanding : 0}
          currency={currency}
          icon={FiClock}
          tone="bg-amber-50 text-amber-700"
        />
        <SummaryCard
          label="Overdue Amount"
          value={showingData ? summary.overdue : 0}
          currency={currency}
          icon={FiAlertCircle}
          tone="bg-rose-50 text-rose-700"
        />
      </div>

      <ClientInvoiceFilters
        {...filters}
        onChange={updateFilter}
        onReset={resetFilters}
      />

      <ClientInvoiceTable
        invoices={filteredInvoices}
        loading={loading}
        page={page}
        pageSize={PAGE_SIZE}
        currency={currency}
        onPageChange={setPage}
        onView={setSelectedInvoice}
      />

      {!loading && showingData && invoices.length === 0 && !error && (
        <p className="text-center text-xs text-slate-500">
          No invoices have been recorded for this business.
        </p>
      )}

      <ClientInvoiceDetailsModal
        organizationId={organizationId}
        invoice={selectedInvoice}
        currency={currency}
        onClose={() => setSelectedInvoice(null)}
      />
    </div>
  );
}